import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { sortParticipants } from 'utils/campaignParticipants';
import {
  MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION,
  getCustomOfferFields,
  getSelectedSubcampaigns
} from 'utils/bulk-offer';
import { logError } from 'utils/log';

import type {
  Action,
  Deliverable,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignCreateBulkOfferCustomField,
  SubcampaignCreateBulkOfferCustomizedFieldsMap,
  SubcampaignOfferForm,
  SubcampaignSelectedIdsMap,
  ThunkAction
} from 'types';

const LOADING = 'bulkOffer/LOADING';
const LOADED = 'bulkOffer/LOADED';
const RESET = 'bulkOffer/RESET';
const SAVE_SELECTED = 'bulkOffer/SAVE_SELECTED';
const SAVE_CUSTOMIZED_FIELDS = 'bulkOffer/SAVE_CUSTOMIZED_FIELDS';
const RESET_CUSTOMIZED_FIELD = 'bulkOffer/RESET_CUSTOMIZED_FIELD';
const SET_CONTENT_TYPES = 'bulkOffer/SET_CONTENT_TYPES';

const { CAMPAIGNS } = API.END_POINTS;

const initialState: SubcampaignBulkOfferStore = {
  isLoading: false,
  isActive: false,
  shortlistedSubcampaigns: [],
  selectedIdsMap: {},
  customizedFieldsByIdMap: {},
  contentTypes: []
};

function setLoadingStatus(status: boolean): Action {
  return {
    type: LOADING,
    payload: {
      status
    }
  };
}

export function resetBulkOffer() {
  return {
    type: RESET,
    payload: {}
  };
}

export function receiveShortlistedSubcampaigns(shortlistedSubcampaigns: Array<Subcampaign>): Action {
  return {
    type: LOADED,
    payload: {
      shortlistedSubcampaigns
    }
  };
}

export function saveCurrentSelectedSubcampaigns(selectedIdsMap: SubcampaignSelectedIdsMap): Action {
  return {
    type: SAVE_SELECTED,
    payload: {
      selectedIdsMap
    }
  };
}

export function saveCurrentCustomizedFields(customizedFieldsByIdMap: SubcampaignCreateBulkOfferCustomizedFieldsMap): Action {
  return {
    type: SAVE_CUSTOMIZED_FIELDS,
    payload: {
      customizedFieldsByIdMap
    }
  };
}

export function resetCustomizedField(fieldName: SubcampaignCreateBulkOfferCustomField): Action {
  return {
    type: RESET_CUSTOMIZED_FIELD,
    payload: {
      fieldName
    }
  };
}

export function fetchShortlistedSubcampaigns(campaignId: number): ThunkAction {
  const url = `${CAMPAIGNS.getCampaignSubcampaigns(campaignId)}?states=draft`;

  return (dispatch) => {
    dispatch(setLoadingStatus(true));

    return get(url)
      .then((response: any) => {
        dispatch(receiveShortlistedSubcampaigns(sortParticipants(response.data)));
        dispatch(setLoadingStatus(false));
      })
      .catch((error) => {
        dispatch(setLoadingStatus(false));

        throw error;
      });
  };
}

export function performBulkOfferCreation(
  campaignId: number,
  subcampaignIds: Array<number>,
  offer: SubcampaignOfferForm,
  deliverables: Array<Deliverable>
): ThunkAction {
  const url = CAMPAIGNS.createBulkOffers(campaignId);

  return async (dispatch, getState) => {
    dispatch(showLoading(url));

    const customizedFieldsByIdMap = getState().bulkOffer.customizedFieldsByIdMap;
    const preparedFieldsByIdMap = getCustomOfferFields(
      subcampaignIds, offer, deliverables, customizedFieldsByIdMap
    );

    // backend requires id to be null for both offer and deliverables
    const data = {
      offer: {
        ...offer,
        id: null,
        deliverables: deliverables.map((deliverable) => ({
          ...deliverable,
          id: null,
          offerId: null
        }))
      },
      customOffers: preparedFieldsByIdMap
    };

    try {
      await post(url, { data }, { suppressGlobalNotification: true });

      addUserAction({
        eventCategory: 'bulk-offer',
        eventAction: 'bulk-offer-sent'
      });
      await dispatch(resetBulkOffer());
      await dispatch(hideLoading(url));
    } catch (e) {
      logError(e);
      await dispatch(hideLoading(url));
      return Promise.reject(e);
    }

    return Promise.resolve();
  };
}

export function setBulkOfferContentTypes(types: Array<string>): Action {
  return {
    type: SET_CONTENT_TYPES,
    payload: {
      types
    }
  };
}

export default createReducer<SubcampaignBulkOfferStore, any>('bulkOffer', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { shortlistedSubcampaigns }) => {
    const selectedIdsMap = shortlistedSubcampaigns.reduce((existingIdsMap, s) => {
      existingIdsMap[s.id] = state.selectedIdsMap[s.id] || false; // eslint-disable-line no-param-reassign
      return existingIdsMap;
    }, {});
    return {
      ...state,
      shortlistedSubcampaigns,
      selectedIdsMap,
      isActive: getSelectedSubcampaigns(shortlistedSubcampaigns, selectedIdsMap).length >= MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION
    };
  },
  [SAVE_SELECTED]: (state, { selectedIdsMap }) => ({
    ...state,
    selectedIdsMap,
    isActive: getSelectedSubcampaigns(state.shortlistedSubcampaigns, selectedIdsMap).length >= MIN_SUBCAMPAIGNS_COUNT_FOR_BULK_CREATION
  }),
  [SAVE_CUSTOMIZED_FIELDS]: (state, { customizedFieldsByIdMap }) => ({
    ...state,
    customizedFieldsByIdMap
  }),
  [RESET_CUSTOMIZED_FIELD]: (state, { fieldName }) => ({
    ...state,
    customizedFieldsByIdMap: Object.keys(state.customizedFieldsByIdMap).reduce((customizedFieldsByIdMap, subcampaignId) => {
      customizedFieldsByIdMap[subcampaignId] = { // eslint-disable-line no-param-reassign
        ...state.customizedFieldsByIdMap[subcampaignId],
        [fieldName]: null
      };

      return customizedFieldsByIdMap;
    }, {})
  }),
  [SET_CONTENT_TYPES]: (state, { types }) => ({
    ...state,
    contentTypes: types
  })
});
